/* eslint-disable max-len */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReferenceApplicability } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/response/GetIAMReferenceApplicabilityResponse';
import { RootState } from 'app/AppStore';
import { ReactComponent as ComingSoonLogo } from 'assets/CarBrand/brand_coming_soon.svg';
import { Buffer, CircleXMark } from 'assets/icons';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton';
import { DataContainer } from 'components/DataContainer';
import {
  formatReferenceLabel,
  fetchIAMReferenceApplicabilityRequestSaga,
  getReferenceApplicabilities,
  IAMReferenceLocal,
} from 'domains/references';
import { SIDEBAR_MODAL_ZINDEX, theme } from 'styles';
import { Flex, Icon, IconButton, Image, MarginBox, PaddingBox, Text, URL } from 'UI';
import { ExtendedMenuItem } from 'UI/Menu/Menu';
import { getSearchData } from 'utils';
import { SDivider, SFlex, SMenu, SModal } from './IAMReferenceApplicability.styled';

const findVersionsByCode = (targetCode: string, items: ReferenceApplicability[] = []): string[] => {
  for (const item of items) {
    for (const model of item.models) {
      if (model.code === targetCode) {
        return model.versions ?? [];
      }
    }
  }
  return [];
};

export const IAMReferenceApplicability = ({
  size = 16,
  reference,
  primaryDisplayName,
  secondaryDisplayName,
  renderPriceSection,
}: {
  reference: IAMReferenceLocal;
  primaryDisplayName: React.ReactNode;
  secondaryDisplayName: React.ReactNode;
  renderPriceSection: () => React.ReactNode;
  size?: number;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [activeVersions, setActiveVersions] = useState<string[]>([]);
  const referenceNumber = reference.referenceNumber;

  const refApplicabilities = useSelector((state: RootState) => getReferenceApplicabilities(state, referenceNumber));
  const refApplicabilitiesData = getSearchData(refApplicabilities);

  const handleOnClick = () => {
    if (!isOpen) {
      dispatch(fetchIAMReferenceApplicabilityRequestSaga({ reference }));
    }
    setIsOpen(true);
  };

  const handleOnCancel = () => {
    setIsOpen(false);
    setActiveVersions([]);
  };

  const items: ExtendedMenuItem[] = useMemo(
    () =>
      (refApplicabilitiesData || []).map((refApplicabilityData) => ({
        key: refApplicabilityData.brand.code,
        icon: (
          <Image
            src={refApplicabilityData.brand.imgUrl}
            type={URL}
            alt={`reference-applicability-${refApplicabilityData.brand.code}`}
            height={60}
            fallbackComponent={<Icon IconComponent={ComingSoonLogo} size={60} noPointer />}
          />
        ),
        label: refApplicabilityData.brand.label,
        children: refApplicabilityData.models.map((vehicleData) => ({
          key: vehicleData.code,
          label: vehicleData.label,
          versions: vehicleData.versions,
        })),
      })),
    [refApplicabilitiesData],
  );

  return (
    <>
      <IconButton
        IconComponent={Buffer}
        tooltip={t('catalog.reference_card.applicability.tooltip', 'Show reference applicability')}
        round
        size={size}
        onClick={handleOnClick}
        hoverFill={'white'}
        data-cy={'show-reference-applicability-button'}
      />
      <SModal
        footer={null}
        open={isOpen}
        zIndex={SIDEBAR_MODAL_ZINDEX}
        onCancel={handleOnCancel}
        closeIcon={<Icon IconComponent={CircleXMark} color={theme.color.grey15} size={25} />}
        closable
      >
        <SFlex wrap={'wrap'} direction={'column'}>
          <Flex justify={'space-between'} size={0}>
            <Text type="heading3">{t('catalog.reference_card.applicability.title', 'Applicabilities')}</Text>
          </Flex>
          <PaddingBox pt={20} />
          <Flex justify={'space-between'} size={0}>
            <Text type="base" color="grey35">
              {t(
                'catalog.reference_card.applicability.description',
                'All offers listed have the same or substantially the same exterior appearance ' +
                  'and performance characteristics as the manufacturer parts.',
              )}
            </Text>
          </Flex>
          <PaddingBox pt={20} />
          <Flex direction={'row'} size={0}>
            <Flex direction={'column'} align={'flex-start'}>
              <Flex size={0} align={'center'}>
                <Text type="heading3" disableGutter>
                  {formatReferenceLabel(
                    t('catalog.reference_card.reference_number', 'Ref:'),
                    referenceNumber.toUpperCase(),
                  )}
                </Text>
                <MarginBox ml={10} />
                <CopyToClipboardButton
                  textToCopy={referenceNumber}
                  message={t(
                    'catalog.reference_card.reference_number.copied_to_clipboard',
                    'Reference number {{referenceNumber}} copied to clipboard',
                    { referenceNumber },
                  )}
                />
              </Flex>
              <Flex wrap={'wrap'}>
                {primaryDisplayName}
                {secondaryDisplayName}
              </Flex>
            </Flex>
            <MarginBox mr={15} />
            <Flex direction={'row'} justify={'flex-end'} size={1}>
              {renderPriceSection()}
            </Flex>
          </Flex>
          <SDivider />
          <PaddingBox pt={24} />
          <DataContainer data={refApplicabilities?.searchStatus}>
            <Flex direction={'row'}>
              <Flex direction={'column'}>
                <SMenu
                  items={items}
                  onSelect={(item) => {
                    setActiveVersions(findVersionsByCode(item.key, refApplicabilitiesData));
                  }}
                />
              </Flex>
              <Flex direction={'column'}>
                {activeVersions.map((e) => (
                  <PaddingBox px={24} py={5} key={e}>
                    <Text type={'text-dim'}>{e}</Text>
                  </PaddingBox>
                ))}
              </Flex>
            </Flex>
          </DataContainer>
        </SFlex>
      </SModal>
    </>
  );
};
